import { useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, ArrowLeft, MessageCircle, Mail } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";
import { trackEvent, trackPageView } from "@/lib/tracking";

const COPY = {
  ar: {
    eyebrow: "تم الاستلام بسرّية تامة",
    title: "شكراً لك.",
    lead:
      "وصل طلبك إلى مكتب كارول. سنتواصل خلال 24 ساعة بأقصى سرّية.",
    nextTitle: "ما الذي يحدث بعد ذلك",
    next: [
      "مراجعة شخصية لطلبك من قِبل مكتب كارول.",
      "تواصل خاص خلال 24 ساعة عبر القناة المختارة.",
      "محادثة استكشافية سرّية — بدون التزام.",
    ],
    waCta: "تابعنا عبر واتساب الخاص",
    backCta: "العودة إلى الصفحة الرئيسية",
    discreetNote:
      "كل الاتصالات تخضع لاتفاقيات سرّية كاملة.",
  },
  en: {
    eyebrow: "Received in complete confidentiality",
    title: "Thank you.",
    lead:
      "Your request has reached Carol's office. We will reply within 24 hours in complete discretion.",
    nextTitle: "What happens next",
    next: [
      "Personal review by Carol's office.",
      "Private outreach within 24 hours via your preferred channel.",
      "An exploratory, confidential conversation — without commitment.",
    ],
    waCta: "Continue via private WhatsApp",
    backCta: "Return to home",
    discreetNote: "All communication is governed by full confidentiality agreements.",
  },
};

export default function ThankYou() {
  const [params] = useSearchParams();
  const variant = params.get("lang") === "ar" ? "ar" : "en";
  const leadType = params.get("lead_type") || "unknown";
  const c = COPY[variant];
  const isAr = variant === "ar";

  // Conversion tracking — fires on page mount (after redirect from /sa form)
  useEffect(() => {
    trackPageView(`/sa/thank-you?lang=${variant}`);
    trackEvent("conversion_lead", {
      lead_type: leadType,
      locale: variant,
      page: "/sa/thank-you",
      // Standard event names recognised by GA4 + Meta Pixel mappings
      event_category: "lead",
      event_label: "saudi_landing",
      value: 1,
    });
    // RTL handling
    const html = document.documentElement;
    const prevLang = html.getAttribute("lang") || "en";
    const prevDir = html.getAttribute("dir") || "ltr";
    html.setAttribute("lang", variant);
    html.setAttribute("dir", isAr ? "rtl" : "ltr");
    return () => {
      html.setAttribute("lang", prevLang);
      html.setAttribute("dir", prevDir);
    };
  }, [variant, isAr, leadType]);

  return (
    <div data-testid="page-thank-you" className="min-h-screen bg-[#070E13] text-[#F4EEE8] flex items-center justify-center px-6 py-24">
      <div className="absolute inset-0 motif-grid opacity-25 pointer-events-none" />
      <div className="absolute inset-0 motif-hex opacity-20 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(199,162,108,0.10), transparent 60%)" }} />

      <Reveal className="relative max-w-2xl w-full">
        <div className="relative bg-[#0C1821] border border-[#C7A26C]/30 p-10 sm:p-14">
          <GoldCorners size={14} inset={-2} />

          {/* Check icon */}
          <div className="w-16 h-16 rounded-full border border-[#C7A26C]/50 flex items-center justify-center mx-auto">
            <Check size={26} className="text-[#C7A26C]" strokeWidth={1.5} />
          </div>

          <p className="overline gold-text text-center mt-8">{c.eyebrow}</p>
          <h1 className="h-editorial text-5xl sm:text-6xl mt-5 text-center">{c.title}</h1>
          <p className="mt-6 text-[#D6D1C9] text-lg leading-relaxed text-center max-w-xl mx-auto">
            {c.lead}
          </p>

          {/* What happens next */}
          <div className="mt-12 pt-10 border-t border-[#C7A26C]/20">
            <p className="overline gold-text">{c.nextTitle}</p>
            <ol className="mt-6 space-y-4">
              {c.next.map((step, i) => (
                <li key={i} className="flex gap-4 items-start text-[#D6D1C9]">
                  <span className="font-serif text-2xl gold-text leading-none shrink-0 w-7">0{i + 1}</span>
                  <span className="leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* CTAs */}
          <div className="mt-12 flex flex-wrap gap-4 justify-center">
            <a
              href="https://wa.me/971558441468"
              target="_blank"
              rel="noreferrer"
              data-testid="thank-you-whatsapp"
              onClick={() => trackEvent("whatsapp_click", { source: "thank_you", locale: variant, lead_type: leadType })}
              className="cta-premium inline-flex items-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium"
            >
              <MessageCircle size={16} /> {c.waCta}
            </a>
            <Link
              to="/"
              data-testid="thank-you-home"
              className="inline-flex items-center gap-2 border border-[#F4EEE8]/35 text-[#F4EEE8] px-9 py-4 text-xs uppercase tracking-[0.24em] hover:border-[#C7A26C] hover:text-[#C7A26C] transition-colors"
            >
              <ArrowLeft size={14} /> {c.backCta}
            </Link>
          </div>

          {/* Direct email — quiet line */}
          <div className="mt-10 pt-8 border-t border-[#C7A26C]/15 text-center">
            <a
              href="mailto:hello@carolstoffella.com"
              data-testid="thank-you-email"
              className="inline-flex items-center gap-2 text-xs text-[#D6D1C9]/80 hover:text-[#C7A26C] tracking-widest uppercase"
            >
              <Mail size={12} /> hello@carolstoffella.com
            </a>
            <p className="mt-4 text-xs text-[#D6D1C9]/55 italic max-w-md mx-auto">{c.discreetNote}</p>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
